'use client';

import React from 'react';
import { useTranslate } from '@tolgee/react';
import { Caption, EmployeeBaseCard, ChevronRightIcon, Text } from '@/design-system';
import {
  CardCategoryLabel,
  CardCategory,
} from '@/design-system/components/Basic/Card/CardCategoryLabel';
import type { FirstAidLog } from '@/types/Api';
import { formatDateToDe } from '@/utils/helpers';

export interface FirstAidCardProps {
  log: FirstAidLog;
  /** Optional href. If not provided, the card is not clickable. */
  to?: string;
  className?: string;
  /** Optional label at bottom (e.g. "Verbandbuch") */
  label?: string;
  /** Optional. Called when the right chevron is clicked. Stops propagation so card link is not followed. */
  onAction?: () => void;
}

export const FirstAidCard: React.FC<FirstAidCardProps> = ({
  log,
  label,
  to,
  className,
  onAction,
}) => {
  const { t } = useTranslate();
  const formattedDate = formatDateToDe(log.incidentDateTime);

  const handleActionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAction?.();
  };

  return (
    <EmployeeBaseCard to={to} className={className}>
      <div className="flex flex-col flex-1 gap-6">
        <div className="flex justify-end shrink-0 pl-4 pr-2">
          {onAction ? (
            <button
              type="button"
              onClick={handleActionClick}
              aria-label={t('ohs-hub.first_aid_log.subtitle')}
            >
              <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
            </button>
          ) : (
            <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
          )}
        </div>

        <div className="flex flex-col gap-2 min-w-0 px-4">
          <Caption weight="medium" className="text-ds-text-3">
            {t('ohs-hub.first_aid_log.date')}
          </Caption>
          <Text size="s" weight="semibold" className="text-ds-text-1">
            {formattedDate}
          </Text>
        </div>
      </div>
      <CardCategoryLabel category={CardCategory.FIRST_AID}>
        {label ?? t('ohs-hub.employeeDetails.firstAidCard.label')}
      </CardCategoryLabel>
    </EmployeeBaseCard>
  );
};
